/**
 * StatusDropdown Component
 * Feature: 019-client-management-fixes - US2/P2
 * 
 * Editable status dropdown with optimistic updates
 * Used in lead and ticket detail views for inline status changes
 */

import { useState } from 'react';

interface StatusDropdownProps<T extends string> {
  currentStatus: T;
  options: readonly T[];
  onUpdate: (newStatus: T) => Promise<void>;
  disabled?: boolean;
  formatLabel?: (status: T) => string;
  getStatusColor?: (status: T) => string;
}

export default function StatusDropdown<T extends string>({
  currentStatus,
  options,
  onUpdate,
  disabled = false,
  formatLabel = (status) => status,
  getStatusColor,
}: StatusDropdownProps<T>) {
  const [isUpdating, setIsUpdating] = useState(false);

  const handleChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newStatus = e.target.value as T;
    if (newStatus === currentStatus) return;

    setIsUpdating(true);
    try {
      await onUpdate(newStatus);
    } catch (error) {
      console.error('Status update failed:', error);
      // Error handling is managed by the parent component via React Query
    } finally {
      setIsUpdating(false);
    }
  };

  const statusColor = getStatusColor?.(currentStatus) || 'bg-gray-100 text-gray-800';

  return (
    <div className="relative inline-block">
      <select
        value={currentStatus}
        onChange={handleChange}
        disabled={disabled || isUpdating}
        className={`
          appearance-none px-3 py-1.5 pr-8 rounded-full text-sm font-medium
          border border-transparent cursor-pointer
          focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent
          disabled:opacity-50 disabled:cursor-not-allowed
          transition-all duration-200
          ${statusColor}
        `}
      >
        {options.map((status) => (
          <option key={status} value={status}>
            {formatLabel(status)}
          </option>
        ))}
      </select>
      
      {/* Dropdown arrow icon */}
      <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
        <svg
          className="h-4 w-4 text-gray-500"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path
            fillRule="evenodd"
            d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
            clipRule="evenodd"
          />
        </svg>
      </div>

      {/* Loading indicator */}
      {isUpdating && (
        <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-75 rounded-full">
          <svg
            className="animate-spin h-4 w-4 text-blue-600"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
        </div>
      )}
    </div>
  );
}

// Helper function to get status badge colors for leads
export function getLeadStatusColor(status: string): string {
  switch (status.toUpperCase()) {
    case 'NEW':
      return 'bg-blue-100 text-blue-800';
    case 'CONTACTED':
      return 'bg-brand-status-contacted-bg text-brand-status-contacted';
    case 'QUALIFIED':
      return 'bg-brand-status-qualified-bg text-brand-status-qualified';
    case 'UNQUALIFIED':
    case 'LOST':
      return 'bg-red-100 text-red-800';
    case 'CONVERTED':
      return 'bg-purple-100 text-purple-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
}

// Helper function to get status badge colors for tickets
export function getTicketStatusColor(status: string): string {
  switch (status.toUpperCase()) {
    case 'OPEN':
      return 'bg-blue-100 text-blue-800';
    case 'INPROGRESS':
    case 'IN PROGRESS':
      return 'bg-brand-status-in-progress-bg text-brand-status-in-progress';
    case 'WAITINGONCLIENT':
    case 'WAITING ON CLIENT':
      return 'bg-orange-100 text-orange-800';
    case 'RESOLVED':
      return 'bg-brand-status-resolved-bg text-brand-status-resolved';
    case 'CLOSED':
      return 'bg-gray-100 text-gray-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
}

// Helper function to format status labels
export function formatStatusLabel(status: string): string {
  // Convert CamelCase to Title Case with spaces
  return status
    .replace(/([A-Z])/g, ' $1')
    .trim()
    .replace(/^./, (str) => str.toUpperCase());
}
